import { useTheme, useUpdateTimers } from "@/hooks"
import {
  type ItemUpdateData,
  type Note,
  SyncItemType,
  SyncMessageType,
  type Tag,
  type Task,
  type UserSettings,
} from "@taskfortress/core"
import type { ClientDataStorageAdapter } from "@taskfortress/core"
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import type { ReactNode } from "react"
import { useAuth } from "../../auth/AuthProvider"
import { ClientDataLocalStorageAdapter } from "../../lib/ClientDataLocalStorageAdapter"
import { createLogger } from "../../lib/logger"
import { SocketConnectionStatus, useSocket } from "../SocketProvider"
import { DataEngine, DataEngineEvents } from "./DataEngine"
import { SyncStatus, useSocketMessageHandler, useSyncStatus } from "./hooks"

// Create a logger instance for the DataProvider
const logger = createLogger("data")

// DataProvider context type
interface DataContextType {
  notes: Note[]
  tasks: Task[]
  tags: Tag[]
  selectedNotesTag: string | null
  setSelectedNotesTag: (tagId: string | null) => void
  notesForSelectedTag: Note[]
  selectedTasksTag: string | null
  setSelectedTasksTag: (tagId: string | null) => void
  tasksForSelectedTag: Task[]
  syncStatus: SyncStatus
  syncProgress: number
  lastSyncTimestamp: number | null
  saveItems: (
    items: Array<{ itemId: string; type: SyncItemType; content: any }>,
    debounceMs?: number,
  ) => Promise<void>
  getItem: (id: string) => Promise<any>
  deleteItem: (itemId: string) => Promise<void>
  userSettings: UserSettings
  updateUserSettings: (settings: Partial<UserSettings>) => Promise<UserSettings>
  updateGlobalTags: (tags: Tag[]) => Promise<void>
}

const DataContext = createContext<DataContextType | null>(null)

export const useData = () => {
  const context = useContext(DataContext)
  if (!context) {
    throw new Error("useData must be used within a DataProvider")
  }
  return context
}

interface DataProviderProps {
  children: ReactNode
  storageProvider?: ClientDataStorageAdapter
}

export const DataProvider = ({
  children,
  storageProvider = new ClientDataLocalStorageAdapter(),
}: DataProviderProps) => {
  const { isAuthenticated, derivedKeys, email } = useAuth()
  const { connectionStatus, send, addMessageListener, removeMessageListener } =
    useSocket()

  // Use the sync status hook
  const { syncState, dispatchSync, pendingActions } = useSyncStatus()

  // State for data arrays
  const [notes, setNotes] = useState<Note[]>([])
  const [tasks, setTasks] = useState<Task[]>([])
  const [tags, setTags] = useState<Tag[]>([])
  const [userSettings, setUserSettings] = useState<UserSettings>(
    {} as UserSettings,
  )

  // Create DataEngine instance (only created once)
  const [dataEngine] = useState<DataEngine>(() => {
    return new DataEngine({
      storageProvider,
      pendingActions,
      logger,
    })
  })

  // Setup DataEngine event listeners
  useEffect(() => {
    const handleNotesUpdated = (updatedNotes: Note[]) => {
      logger.debug("DataEngine event: Notes updated", updatedNotes)
      setNotes(updatedNotes)
    }

    const handleTasksUpdated = (updatedTasks: Task[]) => {
      logger.debug("DataEngine event: Tasks updated", updatedTasks)
      setTasks(updatedTasks)
    }

    const handleTagsUpdated = (updatedTags: Tag[]) => {
      logger.debug("DataEngine event: Tags updated", updatedTags)
      setTags(updatedTags)
    }

    const handleSettingsUpdated = (updatedSettings: UserSettings) => {
      logger.debug("DataEngine event: Settings updated")
      setUserSettings(updatedSettings)
    }

    dataEngine.on(DataEngineEvents.NOTES_UPDATED, handleNotesUpdated)
    dataEngine.on(DataEngineEvents.TASKS_UPDATED, handleTasksUpdated)
    dataEngine.on(DataEngineEvents.TAGS_UPDATED, handleTagsUpdated)
    dataEngine.on(DataEngineEvents.SETTINGS_UPDATED, handleSettingsUpdated)

    return () => {
      dataEngine.off(DataEngineEvents.NOTES_UPDATED, handleNotesUpdated)
      dataEngine.off(DataEngineEvents.TASKS_UPDATED, handleTasksUpdated)
      dataEngine.off(DataEngineEvents.TAGS_UPDATED, handleTagsUpdated)
      dataEngine.off(DataEngineEvents.SETTINGS_UPDATED, handleSettingsUpdated)
    }
  }, [dataEngine])

  // Handle authentication state changes
  useEffect(() => {
    if (
      isAuthenticated &&
      derivedKeys?.encryptionKey &&
      email &&
      !dataEngine.isLoggedIn
    ) {
      dataEngine.userHasLoggedIn(
        email,
        derivedKeys.encryptionKey,
        connectionStatus,
        send,
      )
    } else if (!isAuthenticated && dataEngine.isLoggedIn) {
      dataEngine.userHasLoggedOut()
    }
  }, [
    isAuthenticated,
    derivedKeys?.encryptionKey,
    email,
    connectionStatus,
    send,
    dataEngine,
  ])

  // Update connection state when socket status changes
  useEffect(() => {
    if (isAuthenticated) {
      dataEngine.updateConnectionState(connectionStatus, send)
    }
  }, [connectionStatus, send, isAuthenticated, dataEngine])

  // Function to set the selected note tag in settings
  const setSelectedNotesTag = useCallback(
    async (tagId: string | null) => {
      try {
        await dataEngine.updateUserSettings({
          lastSelectedNoteTag: tagId,
        })
        logger.info(`Updated selected notes tag: ${tagId}`)
      } catch (error) {
        logger.error("Failed to update selected notes tag", { error })
      }
    },
    [dataEngine],
  )

  // Function to set the selected task tag in settings
  const setSelectedTasksTag = useCallback(
    async (tagId: string | null) => {
      try {
        await dataEngine.updateUserSettings({
          lastSelectedTaskTag: tagId,
        })
        logger.info(`Updated selected tasks tag: ${tagId}`)
      } catch (error) {
        logger.error("Failed to update selected tasks tag", { error })
      }
    },
    [dataEngine],
  )

  // Filter notes based on the selected tag
  const notesForSelectedTag = useMemo(() => {
    const selectedTagId = userSettings.lastSelectedNoteTag
    if (!selectedTagId) {
      return notes
    }
    // Find the tag name for the selected tag id
    const selectedTag = tags.find((tag) => tag.id === selectedTagId)
    if (!selectedTag) return notes
    return notes.filter((note) => note.tags.includes(selectedTag.name))
  }, [notes, userSettings.lastSelectedNoteTag, tags])

  // Filter tasks based on the selected tag
  const tasksForSelectedTag = useMemo(() => {
    const selectedTagId = userSettings.lastSelectedTaskTag
    if (!selectedTagId) {
      return tasks
    }
    // Find the tag name for the selected tag id
    const selectedTag = tags.find((tag) => tag.id === selectedTagId)
    if (!selectedTag) return tasks
    return tasks.filter((task) => task.tags.includes(selectedTag.name))
  }, [tasks, userSettings.lastSelectedTaskTag, tags])

  // Handle the debounced update callback for our useUpdateTimers hook
  const handleDebouncedUpdates = useCallback(
    (id: string, data: ItemUpdateData[]) => {
      if (!isAuthenticated || !derivedKeys?.encryptionKey) {
        logger.error(
          "Cannot update items - not authenticated or encryption key missing",
        )
        throw new Error("Authentication or encryption key is not available")
      }

      logger.info("Processing debounced item updates", {
        id,
        count: data.length,
        itemIds: data.map((item) => item.itemId),
      })

      // Convert ItemUpdateData to the format expected by DataEngine
      const items = data.map((item) => ({
        itemId: item.itemId,
        type: item.type as SyncItemType,
        content: item.content,
      }))

      // Use the DataEngine to save items
      dataEngine.saveItems(items)
    },
    [isAuthenticated, derivedKeys, dataEngine],
  )

  // Use our custom hook for managing update timers
  const { scheduleUpdate } = useUpdateTimers<ItemUpdateData[]>(
    handleDebouncedUpdates,
  )

  // Sync with server when socket connects
  useEffect(() => {
    if (
      isAuthenticated &&
      connectionStatus === SocketConnectionStatus.REGISTERED &&
      syncState.syncStatus === SyncStatus.IDLE
    ) {
      // Begin sync process
      const lastSyncTimestamp = dataEngine.getLastSyncTimestamp()
      logger.info("Starting data sync process", {
        lastSyncTimestamp,
        connectionStatus,
      })
      dispatchSync({ type: "SET_STATUS", status: SyncStatus.SYNCING })

      // First, get the total count of items to sync
      const currentTimestamp = lastSyncTimestamp || 0
      send(SyncMessageType.GET_UPDATED_ITEMS_COUNT, {
        timestamp: currentTimestamp,
      })
    }
  }, [
    connectionStatus,
    isAuthenticated,
    syncState.syncStatus,
    send,
    dataEngine,
    dispatchSync,
  ])

  // Function to fetch a batch of items
  const fetchItemsBatch = useCallback(
    (timestamp: number, offset: number, limit: number) => {
      logger.info("Fetching items batch", { timestamp, offset, limit })
      send(SyncMessageType.FETCH_ITEMS_WITH_REVISIONS, {
        timestamp,
        offset,
        limit,
      })
    },
    [send],
  )

  // Use the socket message handler hook
  const { handleSocketMessage } = useSocketMessageHandler({
    dispatchSync,
    dataEngine,
    fetchItemsBatch,
  })

  // Socket message handler for item sync
  useEffect(() => {
    if (
      isAuthenticated &&
      connectionStatus === SocketConnectionStatus.REGISTERED
    ) {
      // Add message listener using the hook's handleSocketMessage
      addMessageListener(handleSocketMessage)

      // Return cleanup function
      return () => {
        removeMessageListener(handleSocketMessage)
      }
    }
  }, [
    isAuthenticated,
    connectionStatus,
    handleSocketMessage,
    addMessageListener,
    removeMessageListener,
  ])

  // Save items with scheduled updates
  const saveItems = useCallback(
    async (
      items: Array<{ itemId: string; type: SyncItemType; content: any }>,
      debounceMs?: number,
    ) => {
      const idString = items.map(({ itemId }) => itemId).join("|")
      scheduleUpdate(idString, items, debounceMs)
    },
    [scheduleUpdate],
  )

  // Get item by ID
  const getItem = useCallback(
    async (id: string) => {
      return dataEngine.getItem(id)
    },
    [dataEngine],
  )

  // Delete item by ID
  const deleteItem = useCallback(
    async (itemId: string) => {
      logger.info("Deleting item", { itemId })

      try {
        await dataEngine.deleteItem(itemId)
        logger.info("Item deleted successfully", { itemId })
      } catch (error) {
        logger.error("Failed to delete item", { error, itemId })
        throw error
      }
    },
    [dataEngine],
  )

  // Update user settings
  const updateUserSettings = useCallback(
    async (settings: Partial<UserSettings>): Promise<UserSettings> => {
      logger.info("Updating user settings", settings)
      return dataEngine.updateUserSettings(settings)
    },
    [dataEngine],
  )

  // Update global tags
  const updateGlobalTags = useCallback(
    async (tags: Tag[]) => {
      logger.info("Updating global tags", tags)
      await dataEngine.updateGlobalTags(tags)
      logger.info("Global tags updated successfully")
    },
    [dataEngine],
  )

  // Use the theme hook to handle theme changes
  useTheme(userSettings.theme!)

  return (
    <DataContext.Provider
      value={{
        notes,
        tasks,
        tags,
        selectedNotesTag: userSettings.lastSelectedNoteTag ?? null,
        setSelectedNotesTag,
        notesForSelectedTag,
        selectedTasksTag: userSettings.lastSelectedTaskTag ?? null,
        setSelectedTasksTag,
        tasksForSelectedTag,
        syncStatus: syncState.syncStatus,
        syncProgress: syncState.syncProgress,
        lastSyncTimestamp: dataEngine.getLastSyncTimestamp(),
        saveItems,
        getItem,
        deleteItem,
        userSettings,
        updateUserSettings,
        updateGlobalTags,
      }}
    >
      {children}
    </DataContext.Provider>
  )
}
